/* eslint-disable react-hooks/rules-of-hooks */
"use client"

import type React from "react"

import {
  Button,
  Input,
  HStack,
  VStack,
  Box,
  Text,
  Spinner,
  SimpleGrid,
  IconButton,
  Tooltip,
  Badge,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { BoxTitle } from "@/components/BoxTitle"
import { ModalAlert } from "@/components/ModalStyled/Modal"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useState } from "react"
import { useFetchItem, useSave } from "../service"
import ModalRetorno from "@/components/components/ModalRetorno"
import { FieldTipoDeposito } from "@/components/Fields/TipoDeposito"
import ActiveSwitch from "@/components/components/ActiveSwitch"

import { FaUpload, FaArrowLeft } from "react-icons/fa"
import { DepositoImport, type ImportDepositoData } from "../Components/import/DepositoImport"

interface DepositoFormData {
  depositoerp: number | null
  nome: string
  tipo: number
  ativo: boolean
  id?: number
}

type ModalMode = "form" | "import"

const ModalDemanda: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const [modalMode, setModalMode] = useState<ModalMode>("form")
  const [isRetornoOpen, setIsRetornoOpen] = useState(false)
  const [mensagemRetorno, setMensagemRetorno] = useState("")
  const [tipoRetorno, setTipoRetorno] = useState<"sucesso" | "erro">("sucesso")
  const [importData, setImportData] = useState<ImportDepositoData[]>([])

  const { handleSubmit, setValue, watch, reset } = useForm<DepositoFormData>({
    defaultValues: {
      depositoerp: null,
      nome: "",
      tipo: 0,
      ativo: true,
    },
  })

  const { data, isLoading } = id ? useFetchItem(Number(id)) : { data: undefined, isLoading: false }


  const save = useSave(id)

  useEffect(() => {
    if (id && data) reset(data)
  }, [id, data, reset])


  const safeNumberConversion = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
      return null
    }

    const numValue = Number(value)
    if (isNaN(numValue)) {
      return null
    }

    return numValue
  }

  const onSubmit = async (formData: DepositoFormData) => {

    const depositoerpValue = safeNumberConversion(formData.depositoerp)
    const tipoValue = safeNumberConversion(formData.tipo)

    if (
      depositoerpValue === null ||
      depositoerpValue <= 0 ||
      !formData.nome.trim() ||
      tipoValue === null ||
      tipoValue <= 0
    ) {
      setMensagemRetorno("Preencha todos os campos obrigatórios com valores válidos.")
      setTipoRetorno("erro")
      setIsRetornoOpen(true)
      return
    }

    const parsedData = {
      ...formData,
      depositoerp: depositoerpValue,
      tipo: tipoValue,
    }

    try {
      await save.mutateAsync(parsedData)
      setMensagemRetorno(isEdit ? "Depósito atualizado com sucesso!" : "Depósito criado com sucesso!")
      setTipoRetorno("sucesso")
      setIsRetornoOpen(true)
    } catch (error) {
      setMensagemRetorno("Erro ao salvar depósito. " + ((error as any)?.message || ""))
      setTipoRetorno("erro")
      setIsRetornoOpen(true)
    }
  }

  const handleImportSubmit = async (data: ImportDepositoData[]) => {
    try {
      const results = []
      for (const item of data) {
        try {
          const depositoerpValue = safeNumberConversion(item.depositoerp)
          const tipoValue = safeNumberConversion(item.tipo)

          const parsedItem: DepositoFormData = {
            depositoerp: depositoerpValue,
            nome: item.nome || "",
            tipo: tipoValue || 0,
            ativo:
              item.ativo !== undefined && item.ativo !== null
                ? typeof item.ativo === "string"
                  ? item.ativo.toLowerCase() === "true"
                  : Boolean(item.ativo)
                : true,
          }

          if (
            parsedItem.depositoerp === null ||
            parsedItem.depositoerp <= 0 ||
            !parsedItem.nome.trim() ||
            parsedItem.tipo <= 0
          ) {
            results.push({ success: false, item, error: new Error("Dados incompletos ou inválidos para importação.") })
            continue
          }
          //@ts-ignore
          await save.mutateAsync(parsedItem)
          results.push({ success: true, item, result: "OK" })
        } catch (error) {
          results.push({ success: false, item, error })
        }
      }

      const successCount = results.filter((r) => r.success).length
      const errorCount = results.filter((r) => !r.success).length

      setMensagemRetorno(
        `Importação concluída! ${successCount} depósitos processados com sucesso${
          errorCount > 0 ? `, ${errorCount} com erro` : ""
        }.`,
      )
      setTipoRetorno(errorCount === 0 ? "sucesso" : "erro")
      setIsRetornoOpen(true)
    } catch (error) {
      setMensagemRetorno("Erro durante a importação: " + ((error as any)?.message || ""))
      setTipoRetorno("erro")
      setIsRetornoOpen(true)
    }
  }

  const handleImportComplete = () => {
    setMensagemRetorno("Importação concluída com sucesso!")
    setTipoRetorno("sucesso")
    setIsRetornoOpen(true)
  }

  const setFileContentForProcess = (data: ImportDepositoData[]) => {
    setImportData(data)
  }

  const getModalTitle = () => {
    if (isEdit) {
      return "Editar Depósito"
    }

    return modalMode === "form" ? "Novo Depósito" : "Importar Depósitos"
  }

  const getModalSize = () => {
    return modalMode === "import" ? "4xl" : "2xl"
  }

  const renderHeader = () => (
    <HStack w={"100%"} justify="space-between" align="center">
      <HStack>
        {modalMode === "import" && !isEdit && (
          <Tooltip label="Voltar ao formulário">
            <IconButton
              aria-label="Voltar ao formulário"
              icon={<FaArrowLeft />}
              size="sm"
              variant="ghost"
              onClick={() => setModalMode("form")}
            />
          </Tooltip>
        )}
        <Text fontSize="xl" ml={2}>
          {getModalTitle()}
        </Text>
        {modalMode === "import" && importData.length > 0 && (
          <Badge colorScheme="blue" ml={2}>
            {importData.length} itens
          </Badge>
        )}
      </HStack>

      <HStack>
        {!isEdit && modalMode === "form" && (
          <Tooltip backgroundColor={"#117BAA"} borderRadius={"25px"} label="Importar em lote">
            <IconButton
              aria-label="Importar em lote"
              icon={<FaUpload />}
              size="sm"
              backgroundColor={"#117BAA"}
              variant="outline"
              onClick={() => setModalMode("import")}
            />
          </Tooltip>
        )}

        {modalMode === "form" && (
          <Box pr={12}>
            <ActiveSwitch size="sm" isChecked={watch("ativo")} onChange={() => setValue("ativo", !watch("ativo"))} />
          </Box>
        )}
      </HStack>
    </HStack>
  )

  const renderBody = () => {
    if (modalMode === "import") {
      return (
        <Box m={2}>
          <DepositoImport
            onImportComplete={handleImportComplete}
            triggerSubmitImport={handleImportSubmit}
            setFileContentForProcess={setFileContentForProcess}
          />
        </Box>
      )
    }

    return (
      <>
        {isEdit && isLoading ? (
          <Box textAlign="center">
            <Spinner />
          </Box>
        ) : (
          <Box m={2} as="form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <BoxTitle title="Código ERP do Depósito *">
                    <Input
                      type="number"
                      value={watch("depositoerp") ?? ""}
                      onChange={(e) => {
                        const val = e.target.value
                        const numValue = safeNumberConversion(val)
                        setValue("depositoerp", numValue)
                      }}
                      placeholder="Digite o código ERP"
                    />
                  </BoxTitle>
                </Box>

                <Box>
                  <BoxTitle title="Nome do Depósito *">
                    <Input
                      value={watch("nome")}
                      onChange={(e) => setValue("nome", e.target.value)}
                      placeholder="Digite o nome"
                    />
                  </BoxTitle>
                </Box>
              </SimpleGrid>

              <Box>
                <FieldTipoDeposito
                  isInvalid={false}
                  error={undefined}
                  //@ts-ignore
                  value={watch("tipo")}
                  onChange={(val: string) => {
                    const numValue = safeNumberConversion(val)
                    setValue("tipo", numValue || 0)
                  }}
                  required
                />
              </Box>
            </VStack>
          </Box>
        )}
      </>
    )
  }

  const renderFooter = () => {
    if (modalMode === "import") {
      return <></>
    }

    return (
      <Button backgroundColor={"#117baa"} onClick={handleSubmit(onSubmit)} isLoading={save.isLoading} color={"white"}>
        {isEdit ? "Salvar Alterações" : "Confirmar"}
      </Button>
    )
  }

  return (
    <>
      <ModalAlert
        isOpen={true}
        xIsback
        size={getModalSize()}
        title={renderHeader()}
        body={renderBody()}
        footer={renderFooter()}
      />

      <ModalRetorno
        isOpen={isRetornoOpen}
        onClose={() => {
          setIsRetornoOpen(false)
          if (tipoRetorno === "sucesso") {
            if (modalMode === "import") {
              setModalMode("form")
              setImportData([])
            } else {
              navigate(-1)
            }
          }
        }}
        mensagem={mensagemRetorno}
        tipo={tipoRetorno}
      />
    </>
  )
}

export default ModalDemanda
